import React, { createContext, useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import isCdsFrequentPassengerEnabled from 'utils/isCdsFrequentPassengerEnabled';
import {
  cdsFrequentPassengerActivation,
  cdsFrequentPassengerEnrollment,
  cdsFrequentPassengerLogin,
  cdsFrequentPassengerLogout,
  cdsFrequentPassengerPasswordUpdate,
  clearCdsFpError,
  clearCdsFpSession,
  fetchCdsFrequentPassengerAccountStatus,
  resetCdsFpEnrollmentFlow,
} from '@/actions/cdsFrequentPassenger';
import { useReserhubAccount } from './ReserhubAccount';

const CdsFrequentPassengerContext = createContext(null);

/**
 * Provider for CDS Pasajero Frecuente loyalty session state.
 * Bootstraps the legacy account status when the feature is enabled without Unified Account.
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @returns {React.ReactElement}
 */
export function CdsFrequentPassengerProvider({ children }) {
  const dispatch = useDispatch();
  const { enabled: reserhubAccountEnabled } = useReserhubAccount();
  const { brand } = useWhitelabelEnvs();
  const features = useSelector((state) => state.whitelabelConfig?.features ?? {});
  const isAuthenticated = useSelector((state) => state.cdsFrequentPassenger.get('isAuthenticated'));
  const isLoading = useSelector((state) => state.cdsFrequentPassenger.get('isLoading'));
  const isBootstrapped = useSelector((state) => state.cdsFrequentPassenger.get('isBootstrapped'));
  const error = useSelector((state) => state.cdsFrequentPassenger.get('error'));
  const accountStatus = useSelector((state) => state.cdsFrequentPassenger.get('accountStatus'));
  const enrollmentStep = useSelector((state) => state.cdsFrequentPassenger.get('enrollmentStep'));
  const enrollmentIdentity = useSelector((state) => {
    return state.cdsFrequentPassenger.get('enrollmentIdentity');
  });
  const activationResult = useSelector((state) => {
    return state.cdsFrequentPassenger.get('activationResult');
  });
  const programToken = useSelector((state) => state.cdsFrequentPassenger.get('programToken'));

  const enabled = isCdsFrequentPassengerEnabled(features, brand);
  const sessionOwner = useRef(null);

  useEffect(() => {
    if (!enabled) return;

    const nextOwner = reserhubAccountEnabled ? 'reserhub' : 'legacy';
    if (sessionOwner.current === nextOwner) return;

    sessionOwner.current = nextOwner;
    if (reserhubAccountEnabled) {
      // Unified Account owns the CDS session. Mark the legacy source as resolved without calling
      // Purchase so anonymous shoppers can still see the checkout opt-in.
      dispatch(clearCdsFpSession({ usesReserhubAccount: true }));
      return;
    }

    // Silent bootstrap: failures (401, network errors) are already surfaced via the
    // `error` Redux state below. The action re-throws for explicit callers (login,
    // activation) to react to, but this background dispatch has no caller to catch
    // it — swallow here so a network failure doesn't reach Sentry as an unhandled
    // rejection.
    dispatch(fetchCdsFrequentPassengerAccountStatus({ silentUnauthorized: true })).catch(() => {});
  }, [dispatch, enabled, reserhubAccountEnabled]);

  const value = {
    enabled,
    isAuthenticated,
    isLoading,
    isBootstrapped,
    error,
    accountStatus,
    enrollmentStep,
    enrollmentIdentity,
    activationResult,
    programToken,
    login: (payload) => dispatch(cdsFrequentPassengerLogin(payload)),
    logout: () => dispatch(cdsFrequentPassengerLogout()),
    fetchAccountStatus: () => dispatch(fetchCdsFrequentPassengerAccountStatus()),
    enrollment: (payload) => dispatch(cdsFrequentPassengerEnrollment(payload)),
    activation: (payload) => dispatch(cdsFrequentPassengerActivation(payload)),
    passwordUpdate: (payload) => dispatch(cdsFrequentPassengerPasswordUpdate(payload)),
    clearError: () => dispatch(clearCdsFpError()),
    resetEnrollmentFlow: () => dispatch(resetCdsFpEnrollmentFlow()),
  };

  return (
    <CdsFrequentPassengerContext.Provider value={value}>
      {children}
    </CdsFrequentPassengerContext.Provider>
  );
}

CdsFrequentPassengerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook for CDS Pasajero Frecuente loyalty session.
 * @returns {object}
 */
export function useCdsFrequentPassenger() {
  const context = useContext(CdsFrequentPassengerContext);

  if (!context) {
    throw new Error('useCdsFrequentPassenger must be used within a CdsFrequentPassengerProvider');
  }

  return context;
}
